// Merged session timeline — vertical list of events from three sources,
// sorted by timestamp.
//
// WHY this component exists:
//   Call Detail already shows Call Flow (business-view narrative), Audit
//   (per-session audit JSON), and per-session Messages. None of those
//   stitch the CC-side contact timeline together with per-session bot /
//   human / tool events on a single clock. For engineers debugging a
//   failed call, the single-clock view is often the fastest way to tell
//   whether a tool fired BEFORE or AFTER the leg disconnect.
//
// SOURCES (merged into a flat events list):
//   * contact-timeline events  → source: 'cc'       (grey dot)
//   * per-session messages:
//       - userType 'bot'       → source: 'bot'      (blue dot)
//       - userType 'human'     → source: 'human'    (teal dot)
//       - userType 'custom_data' → source: 'cc'    (grey dot — these ARE
//                                                  CC-side events wrapped
//                                                  into the message stream)
//   * tool invocations within each bot message:
//       - state === 'SUCCESS'  → source: 'tool'     (violet dot)
//       - state !== 'SUCCESS'  → source: 'tool-error' (red dot)
//
// NO NEW MCP CALLS beyond `get-contact-timeline`:
//   Call Detail already fetches per-session messages (via ConversationTurns
//   / AiSessionTabContent) and the task + legs via `resolveCallByInteractionId`.
//   Contact-timeline, however, is NOT currently fetched in CallDetail —
//   it's only fetched by AgentTroubleshooting. Adding it here is one new
//   MCP call per Call Detail view. The caller passes `events` already
//   fetched; this component is purely presentational.
//
// RENDERING:
//   HH:MM:SS.mmm timestamp, source Badge, one-line summary. Click a row
//   to expand the raw JSON payload. Visually a vertical list with a
//   coloured dot on the left — simpler than Mantine's Timeline component
//   (which assumes a single-colour spine).

import React, { useMemo, useState } from 'react';
import { Alert, Badge, Group, Loader, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconClock } from '@tabler/icons-react';

// Dot colour + label per source.
const SOURCE_META = {
  cc:         { color: 'gray',    label: 'CC' },
  bot:        { color: 'blue',    label: 'Bot' },
  human:      { color: 'teal',    label: 'Caller' },
  tool:       { color: 'violet',  label: 'Tool' },
  'tool-error': { color: 'red',   label: 'Tool error' },
};

export function SessionTimeline({
  contactTimeline,
  contactTimelineLoading,
  contactTimelineError,
  messagesBySession,
  messagesBySessionLoading,
  aiSessions,
}) {
  const events = useMemo(
    () => buildMergedTimeline({ contactTimeline, messagesBySession, aiSessions }),
    [contactTimeline, messagesBySession, aiSessions],
  );

  const anyLoading = contactTimelineLoading || messagesBySessionLoading;

  if (anyLoading && events.length === 0) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Merging CC-timeline + per-session messages — rate-limited at 2.2s per source call.
        </Text>
      </Group>
    );
  }

  return (
    <Stack gap="xs">
      {contactTimelineError && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={14} />}>
          <Text size="xs">CC-timeline unavailable: {contactTimelineError}. Bot and tool events below are still from the per-session fetch.</Text>
        </Alert>
      )}
      {anyLoading && events.length > 0 && (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">More sources still loading — list will fill in.</Text>
        </Group>
      )}
      {events.length === 0 && !anyLoading && (
        <Text size="xs" c="dimmed">No events available for this interaction.</Text>
      )}
      {events.map((e, i) => (
        <TimelineRow key={e.id ?? i} event={e} />
      ))}
    </Stack>
  );
}

function TimelineRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const meta = SOURCE_META[event.source] ?? SOURCE_META.cc;

  return (
    <Paper
      withBorder
      p="xs"
      radius="sm"
      style={{
        cursor: 'pointer',
        borderLeft: `3px solid var(--mantine-color-${meta.color}-5)`,
      }}
      onClick={() => setExpanded((v) => !v)}
    >
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <ThemeIcon size="xs" radius="xl" color={meta.color} variant="filled">
          <IconClock size={10} />
        </ThemeIcon>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" style={{ fontFamily: 'monospace' }} fw={500}>
              {formatHms(event.timestamp)}
            </Text>
            <Badge size="xs" color={meta.color} variant="light">{meta.label}</Badge>
            {event.sessionLabel && (
              <Badge size="xs" color="gray" variant="outline">{event.sessionLabel}</Badge>
            )}
            <Text size="xs" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {event.summary}
            </Text>
          </Group>
          {expanded && event.raw && (
            <pre style={{
              fontSize: 10,
              background: '#f8f9fa',
              padding: 6,
              marginTop: 6,
              marginBottom: 0,
              borderRadius: 4,
              overflow: 'auto',
              maxHeight: 300,
            }}>
              {safeJson(event.raw)}
            </pre>
          )}
        </div>
      </Group>
    </Paper>
  );
}

// HH:MM:SS.mmm — millisecond precision because tool fulfillments can
// come back inside the same second as the leg disconnect, and a human
// trying to order those by eye needs the ms column.
function formatHms(ts) {
  if (ts == null) return '—';
  const n = typeof ts === 'number' ? ts : Number(ts);
  if (!Number.isFinite(n)) {
    // Fall back for an ISO string.
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad3(d.getMilliseconds())}`;
  }
  const d = new Date(n);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad3(d.getMilliseconds())}`;
}
function pad(n) { return String(n).padStart(2, '0'); }
function pad3(n) { return String(n).padStart(3, '0'); }

function safeJson(v) {
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

// Compose events from the three sources into a single sorted array.
// Each event has:
//   { timestamp (number ms), source, summary, sessionLabel?, raw, id }
function buildMergedTimeline({ contactTimeline, messagesBySession, aiSessions }) {
  const events = [];

  // 1. Contact-timeline events (CC-side).
  for (const ev of contactTimeline?.events ?? []) {
    const ts = parseAnyTimestamp(ev.timestamp ?? ev.time ?? ev.createdAt ?? ev.at);
    if (ts == null) continue;
    events.push({
      id: `cc-${ts}-${events.length}`,
      timestamp: ts,
      source: 'cc',
      summary: ev.type || ev.eventType || 'contact-timeline event',
      raw: ev,
    });
  }

  // 2. Per-session messages — one call to the shape mapper per message.
  const sessionLookup = new Map((aiSessions ?? []).map((s) => [s.sessionId, s]));
  for (const [sessionId, msgs] of Object.entries(messagesBySession ?? {})) {
    const sess = sessionLookup.get(sessionId);
    const sessionLabel = sess?.agentName || sessionId.slice(0, 8);
    for (const m of msgs ?? []) {
      const ts = parseAnyTimestamp(m.createdAt ?? m.timestamp ?? m.ts);
      if (ts == null) continue;

      const role = m.userType ?? 'unknown';
      const source =
        role === 'bot' ? 'bot' :
        role === 'human' ? 'human' :
        'cc'; // custom_data or unknown — treat as a CC-side event

      // 2a. The message itself.
      const summary = buildMessageSummary(m, role);
      events.push({
        id: `${sessionId}-${m.id ?? ts}-${events.length}`,
        timestamp: ts,
        source,
        summary,
        sessionLabel,
        raw: m,
      });

      // 2b. Each tool invocation inside a bot message becomes its OWN row,
      // because latency + error state is important to see per-tool. Place
      // tool events slightly after the parent message (ts + 1ms per tool)
      // so stable-sort keeps them visually grouped under the message.
      const tools = Array.isArray(m?.metadata?.tools) ? m.metadata.tools : [];
      tools.forEach((t, i) => {
        const toolName = t.tool_name ?? t.name ?? '(unknown tool)';
        const toolState = t.state ?? '';
        const toolSrc = toolState && toolState !== 'SUCCESS' ? 'tool-error' : 'tool';
        const latency = typeof t.latency === 'number' ? ` · ${(t.latency / 1000).toFixed(2)}s` : '';
        events.push({
          id: `${sessionId}-${m.id ?? ts}-tool-${i}`,
          timestamp: ts + (i + 1), // ms offset to keep tools after the message
          source: toolSrc,
          summary: `${toolName} · state=${toolState || 'unset'}${latency}`,
          sessionLabel,
          raw: t,
        });
      });
    }
  }

  // 3. Sort stable by timestamp (JS Array.sort is stable in modern engines).
  events.sort((a, b) => a.timestamp - b.timestamp);
  return events;
}

function buildMessageSummary(m, role) {
  if (role === 'bot') {
    const classifications = Array.isArray(m?.metadata?.classification) ? m.metadata.classification : [];
    const intents = classifications
      .map((c) => (typeof c === 'string' ? c : c?.intent ?? c?.name ?? null))
      .filter(Boolean);
    if (intents.length) return `bot turn · classified as ${intents.join(', ')}`;
    const latency = typeof m?.metadata?.latency === 'number' ? ` (${(m.metadata.latency / 1000).toFixed(2)}s)` : '';
    return `bot turn${latency}`;
  }
  if (role === 'human') return 'caller turn';
  if (role === 'custom_data') return `custom_data · ${m?.metadata?.event ?? m?.event ?? 'system event'}`;
  return `${role} turn`;
}

// Accepts numbers (ms epoch), ISO strings, or seconds-since-epoch. Returns
// ms epoch or null.
function parseAnyTimestamp(v) {
  if (v == null) return null;
  if (typeof v === 'number') {
    // Heuristic: >1e12 → ms, else seconds. Prod epochs land in both
    // depending on source (contact-timeline is sometimes seconds).
    return v > 1e12 ? v : v * 1000;
  }
  const n = Number(v);
  if (Number.isFinite(n)) return n > 1e12 ? n : n * 1000;
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.getTime();
  return null;
}
